import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { getPlatformSpecs } from '../../constants/platformSpecs';
import CharacterCounter from './CharacterCounter';
import ImageCropPreview from './ImageCropPreview';
import styles from './LinkedInPreview.module.scss';

const LinkedInPreview = ({
  user,
  content,
  title,
  hashtags = [],
  imageUrl,
  onRemoveImage,
  contentType,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const specs = getPlatformSpecs('linkedin');
  const truncateAt = specs?.previewTruncateAt || 210;

  const stripHtml = (html) => {
    if (!html) return '';
    // Convert block elements to newlines before stripping
    let text = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<p[^>]*>/gi, '')
      .replace(/<\/div>/gi, '\n')
      .replace(/<div[^>]*>/gi, '')
      .replace(/<\/li>/gi, '\n')
      .replace(/<li[^>]*>/gi, '• ');

    // Strip remaining HTML tags
    const tmp = document.createElement('DIV');
    tmp.innerHTML = text;
    text = tmp.textContent || tmp.innerText || '';

    // Clean up excessive newlines
    return text.replace(/\n{3,}/g, '\n\n').trim();
  };

  const plainContent = stripHtml(content);
  const charCount = plainContent.length;
  const shouldTruncate = charCount > truncateAt && !isExpanded;
  const displayContent = shouldTruncate
    ? `${plainContent.slice(0, truncateAt)}...`
    : plainContent;

  const getUserInitials = () => {
    if (!user) return 'U';
    const firstName = user.first_name || user.name?.split(' ')[0] || '';
    const lastName = user.last_name || user.name?.split(' ')[1] || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  const getUserName = () => {
    if (!user) return 'User';
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.name || user.email || 'User';
  };

  return (
    <div className={styles.linkedinPreview}>
      {/* Preview Header */}
      <div className={styles.previewHeader}>
        <span className={styles.previewTitle}>LinkedIn Post Preview</span>
      </div>

      {/* API Limitation Banner */}
      <div className={styles.warningBanner}>
        <svg className={styles.warningIcon} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
        </svg>
        <span>
          LinkedIn API only supports publishing Posts. Articles cannot be published via API.
        </span>
      </div>

      {/* Post Header */}
      <div className={styles.header}>
        <div className={styles.avatar}>
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt={getUserName()} />
          ) : (
            <span>{getUserInitials()}</span>
          )}
        </div>
        <div className={styles.userInfo}>
          <div className={styles.userName}>
            {getUserName()}
            <span className={styles.connection}> • 1st</span>
          </div>
          <div className={styles.userHeadline}>
            {user?.headline || user?.role || 'marketer'}
          </div>
          <div className={styles.postMeta}>
            <span>Now</span>
            <span className={styles.dot}>•</span>
            {/* LinkedIn Globe Icon */}
            <svg className={styles.globeIcon} viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 1a7 7 0 107 7 7 7 0 00-7-7zM3 8a5 5 0 011-3l.55.55A1.5 1.5 0 015 6.62v1.07a.75.75 0 00.22.53l.56.56a.75.75 0 00.53.22H7v.69a.75.75 0 00.22.53l.56.56a.75.75 0 01.22.53V13a5 5 0 01-5-5zm6.24 4.83l2-2.46a.75.75 0 00.09-.8l-.58-1.16A.76.76 0 0010 8H7v-.19a.51.51 0 01.28-.45l.38-.19a.74.74 0 01.68 0l.38.19a.74.74 0 00.68 0 .75.75 0 00.37-.65V5.5a.75.75 0 01.75-.75h.79a5 5 0 01-1.87 8.08z" />
            </svg>
          </div>
        </div>
        <button className={styles.moreButton} aria-label="More options">
          {/* LinkedIn Three Dots Icon */}
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M3 9.5A1.5 1.5 0 114.5 8 1.5 1.5 0 013 9.5zM11.5 8A1.5 1.5 0 1013 6.5 1.5 1.5 0 0011.5 8zm-5 0A1.5 1.5 0 108 6.5 1.5 1.5 0 006.5 8z" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {title && <div className={styles.title}>{title}</div>}
        <div className={styles.text}>
          {displayContent}
          {charCount > truncateAt && (
            <span
              className={styles.seeMore}
              onClick={() => setIsExpanded(!isExpanded)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'see less' : 'see more'}
            </span>
          )}
        </div>

        {/* Hashtags */}
        {hashtags.length > 0 && (
          <div className={styles.hashtags}>
            {hashtags.slice(0, 5).map((tag, index) => (
              <span key={index} className={styles.hashtag}>
                {tag.startsWith('#') ? tag : `#${tag}`}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Image */}
      {imageUrl && (
        <div className={styles.imageContainer}>
          <ImageCropPreview
            imageUrl={imageUrl}
            platformType="linkedin"
            onRemove={onRemoveImage}
          />
        </div>
      )}

      {/* Reactions Row */}
      <div className={styles.engagement}>
        <div className={styles.reactions}>
          <div className={styles.reactionIcons}>
            {/* LinkedIn Like (Blue) */}
            <span className={styles.reactionIcon} style={{ zIndex: 3 }}>
              <svg viewBox="0 0 16 16" width="16" height="16">
                <g>
                  <circle cx="8" cy="8" r="8" fill="#378FE9" />
                  <path fill="#fff" d="M11.2 5.6l-2.1 2.2L7 5.7l-2.1 2.2.8.7 1.3-1.4 2.1 2.1 2.9-3-.8-.7z" transform="translate(0.5, 1)" />
                </g>
              </svg>
            </span>
            {/* LinkedIn Celebrate (Green) */}
            <span className={styles.reactionIcon} style={{ zIndex: 2, marginLeft: '-4px' }}>
              <svg viewBox="0 0 16 16" width="16" height="16">
                <g>
                  <circle cx="8" cy="8" r="8" fill="#6DAE4F" />
                  <path fill="#fff" d="M6.5 4.5l1 2.5h2l-1.5 2 .5 2.5-2-1.5-2 1.5.5-2.5-1.5-2h2l1-2.5z" transform="translate(1.5, 0.5)" />
                </g>
              </svg>
            </span>
            {/* LinkedIn Love (Red) */}
            <span className={styles.reactionIcon} style={{ zIndex: 1, marginLeft: '-4px' }}>
              <svg viewBox="0 0 16 16" width="16" height="16">
                <g>
                  <circle cx="8" cy="8" r="8" fill="#DF704D" />
                  <path fill="#fff" d="M8 12.5l-.5-.5C5 9.5 3.5 8 3.5 6.25 3.5 4.75 4.75 3.5 6.25 3.5c.85 0 1.65.4 2.15 1h.2c.5-.6 1.3-1 2.15-1 1.5 0 2.75 1.25 2.75 2.75 0 1.75-1.5 3.25-4 5.75l-.5.5z" transform="translate(0, 0.5)" />
                </g>
              </svg>
            </span>
          </div>
        </div>
      </div>

      {/* Action Bar - Authentic LinkedIn Icons */}
      <div className={styles.actionBar}>
        <button className={styles.actionButton}>
          {/* LinkedIn Like Icon */}
          <svg viewBox="0 0 24 24" fill="currentColor" className={styles.actionIcon}>
            <path d="M19.46 11l-3.91-3.91a7 7 0 01-1.69 2.74l2.9 2.9a.76.76 0 01-.53 1.27H14v6.5a.75.75 0 01-.75.75h-5.5a.75.75 0 01-.75-.75v-6.5a.75.75 0 01.75-.75h3.5a.75.75 0 00.75-.75V6.75a.75.75 0 01.75-.75h1.5a.75.75 0 01.75.75V9.5c0 .138.112.25.25.25H19.46z" />
          </svg>
          <span>Like</span>
        </button>
        <button className={styles.actionButton}>
          {/* LinkedIn Comment Icon */}
          <svg viewBox="0 0 24 24" fill="currentColor" className={styles.actionIcon}>
            <path d="M7 9h10v1H7V9zm0 4h7v1H7v-1zm13-9H4c-1.1 0-2 .9-2 2v11.17l4-4H20c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 10H5.17L4 15.17V6h16v8z" />
          </svg>
          <span>Comment</span>
        </button>
        <button className={styles.actionButton}>
          {/* LinkedIn Repost Icon */}
          <svg viewBox="0 0 24 24" fill="currentColor" className={styles.actionIcon}>
            <path d="M17.53 10.47l-4-4a.75.75 0 00-1.06 1.06L15.94 11H7.75A2.75 2.75 0 005 13.75v3.5a.75.75 0 001.5 0v-3.5c0-.69.56-1.25 1.25-1.25h8.19l-3.47 3.47a.75.75 0 101.06 1.06l4-4a.75.75 0 000-1.06z" />
            <path d="M6.47 13.53l4 4a.75.75 0 001.06-1.06L8.06 13h8.19c.69 0 1.25-.56 1.25-1.25v-3.5a.75.75 0 00-1.5 0v3.5c0 .69-.56 1.25-1.25 1.25H6.56l3.47-3.47a.75.75 0 00-1.06-1.06l-4 4a.75.75 0 000 1.06z" />
          </svg>
          <span>Repost</span>
        </button>
        <button className={styles.actionButton}>
          {/* LinkedIn Send Icon */}
          <svg viewBox="0 0 24 24" fill="currentColor" className={styles.actionIcon}>
            <path d="M21 3L0 10l7.66 4.26L16 8l-6.26 8.34L14 24l7-21z" />
          </svg>
          <span>Send</span>
        </button>
      </div>

      {/* Character Counter */}
      <div className={styles.counterSection}>
        <CharacterCounter charCount={charCount} platformType="linkedin" />
      </div>
    </div>
  );
};

LinkedInPreview.propTypes = {
  user: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    avatar_url: PropTypes.string,
    headline: PropTypes.string,
    role: PropTypes.string,
  }),
  content: PropTypes.string,
  title: PropTypes.string,
  hashtags: PropTypes.arrayOf(PropTypes.string),
  imageUrl: PropTypes.string,
  onRemoveImage: PropTypes.func,
  contentType: PropTypes.string,
};

export default LinkedInPreview;
